import React, { useState, useRef } from 'react';
import './ImageContainer.css';
import NumberInputSection from './NumberInputSection';
import SliderSection from './SliderSection';
import ToggleSwitch from './ToggleSwitch';

function ImageContainer({ 
    // Basic Props
    label = "Image",
    fieldName = "image",
    currentImage,
    onUpload,
    isUploading = false,
    setIsUploading,
    
    // Toggle Props (Show/Hide Image)
    showToggle = false,
    toggleLabel = "Show Image",
    toggleFieldName,
    toggleChecked = true,
    onToggleChange,
    
    // Control Configuration
    controls = [],
    editData = {},
    handleInputChange,
    
    // Styling
    compact = false,
    theme = 'default'
}) {
    const [imagePreview, setImagePreview] = useState(currentImage);
    const [controlsExpanded, setControlsExpanded] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // Image Upload Handler
    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (file && onUpload) {
            try {
                // Korrekte Parameter-Reihenfolge für useWidgetEdit
                await onUpload(file, fieldName, setImagePreview, setIsUploading);
            } catch (error) {
                console.error('Upload error:', error);
                setIsUploading(false);
            }
        }
    };

    // Drag & Drop Handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        setDragOver(false);
        
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/') && onUpload) {
            try {
                await onUpload(file, fieldName, setImagePreview, setIsUploading);
            } catch (error) {
                console.error('Upload error:', error);
                setIsUploading(false);
            }
        }
    };

    // Controls Toggle
    const toggleControls = () => {
        setControlsExpanded(!controlsExpanded);
    };

    // Event Adapter for NumberInputSection
    const createEventAdapter = (fieldName) => (field, value) => {
        const fakeEvent = {
            target: {
                name: fieldName,
                value: value,
                type: 'number'
            }
        };
        handleInputChange(fakeEvent);
    };

    // Event Adapter for SliderSection
    const createSliderEventAdapter = (fieldName) => (field, value) => {
        const fakeEvent = {
            target: {
                name: fieldName,
                value: value,
                type: 'range'
            }
        };
        handleInputChange(fakeEvent);
    };

    // Position Grid Component
    const PositionGrid = ({ fieldName, currentValue, onChange }) => {
        const positions = [
            { value: 'top-left', icon: '↖️', label: 'Top Left' },
            { value: 'top-center', icon: '⬆️', label: 'Top Center' },
            { value: 'top-right', icon: '↗️', label: 'Top Right' },
            { value: 'center-left', icon: '⬅️', label: 'Center Left' },
            { value: 'center', icon: '⏺️', label: 'Center' },
            { value: 'center-right', icon: '➡️', label: 'Center Right' },
            { value: 'bottom-left', icon: '↙️', label: 'Bottom Left' },
            { value: 'bottom-center', icon: '⬇️', label: 'Bottom Center' },
            { value: 'bottom-right', icon: '↘️', label: 'Bottom Right' }
        ];

        return (
            <div className="bc-position-grid-container">
                <div className="bc-position-grid">
                    {positions.map((position, index) => (
                        <button
                            key={position.value}
                            type="button"
                            className={`bc-position-btn ${currentValue === position.value ? 'bc-active' : ''}`}
                            onClick={() => onChange(fieldName, position.value)}
                            title={position.label}
                        >
                            <span className="bc-position-icon">{position.icon}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    // Image Effects Component
    const ImageEffects = ({ fieldName, currentValue, onChange }) => {
        const effects = [
            { value: 'none', icon: '✨', label: 'None' },
            { value: 'darken-light', icon: '🌑', label: 'Darken Light' },
            { value: 'darken', icon: '🌑', label: 'Darken Medium' },
            { value: 'darken-heavy', icon: '🌑', label: 'Darken Heavy' },
            { value: 'darken-extreme', icon: '🌑', label: 'Darken Extreme' },
            { value: 'lighten-light', icon: '🌕', label: 'Lighten Light' },
            { value: 'lighten', icon: '🌕', label: 'Lighten Medium' },
            { value: 'lighten-heavy', icon: '🌕', label: 'Lighten Heavy' },
            { value: 'lighten-extreme', icon: '🌕', label: 'Lighten Extreme' },
            { value: 'vignette-light', icon: '⭕', label: 'Vignette Light' },
            { value: 'vignette', icon: '⭕', label: 'Vignette Medium' },
            { value: 'vignette-heavy', icon: '⭕', label: 'Vignette Heavy' },
            { value: 'vignette-extreme', icon: '⭕', label: 'Vignette Extreme' },
            { value: 'grayscale', icon: '⚫', label: 'Grayscale' },
            { value: 'sepia', icon: '🟤', label: 'Sepia' }
        ];

        return (
            <div className="bc-image-effects-container">
                <div className="bc-image-effects-grid">
                    {effects.map((effect) => (
                        <button
                            key={effect.value}
                            type="button"
                            className={`bc-effect-btn ${currentValue === effect.value ? 'bc-active' : ''}`}
                            onClick={() => onChange(fieldName, effect.value)}
                            title={effect.label}
                        >
                            <span className="bc-effect-icon">{effect.icon}</span>
                            <span className="bc-effect-label">{effect.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    // Render Control based on type
    const renderControl = (control, index) => {
        const value = editData[control.fieldName] || control.defaultValue || 0;

        switch (control.type) {
            case 'size':
                // Handle percentage conversion
                let displayValue = value;
                if (control.convertValue && control.unit === '%') {
                    displayValue = Math.round(value * 100);
                }

                return (
                    <div key={index} className="bc-control-item">
                        <NumberInputSection
                            label={control.label}
                            fieldName={control.fieldName}
                            value={displayValue}
                            min={control.min || 0}
                            max={control.max || 100}
                            step={control.step || 1}
                            unit={control.unit || 'px'}
                            description={control.description}
                            onChange={(field, val) => {
                                let finalValue = val;
                                // Convert percentage back to decimal
                                if (control.convertValue && control.unit === '%') {
                                    finalValue = val / 100;
                                }
                                createEventAdapter(field)(field, finalValue);
                            }}
                        />
                    </div>
                );

            case 'slider':
                // Handle percentage conversion for sliders
                let sliderDisplayValue = value;
                if (control.convertValue && control.unit === '%') {
                    sliderDisplayValue = Math.round(value * 100);
                }

                return (
                    <div key={index} className="bc-control-item">
                        <SliderSection
                            label={control.label}
                            fieldName={control.fieldName}
                            value={sliderDisplayValue}
                            min={control.min || 0}
                            max={control.max || 100}
                            step={control.step || 1}
                            unit={control.unit || 'px'}
                            description={control.description}
                            color={control.color || '#5C80BC'}
                            icon={control.icon}
                            onChange={(field, val) => {
                                let finalValue = val;
                                // Convert percentage back to decimal
                                if (control.convertValue && control.unit === '%') {
                                    finalValue = val / 100;
                                }
                                createSliderEventAdapter(field)(field, finalValue);
                            }}
                        />
                    </div>
                );

            case 'toggle':
                return (
                    <div key={index} className="bc-control-item">
                        <div className="bc-toggle-control">
                            <label className="bc-control-label">{control.label}</label>
                            <ToggleSwitch
                                fieldName={control.fieldName}
                                checked={editData[control.fieldName] || false}
                                onChange={handleInputChange}
                                size="small"
                                color="#5C80BC"
                                showLabel={false}
                            />
                        </div>
                    </div>
                );

            case 'select':
                return (
                    <div key={index} className="bc-control-item">
                        <label className="bc-control-label">{control.label}</label>
                        <select
                            className="bc-control-select"
                            value={editData[control.fieldName] || control.defaultValue || ''}
                            onChange={(e) => handleInputChange({ target: { name: control.fieldName, value: e.target.value } })}
                        >
                            {control.options?.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                );

            case 'position-grid':
                return (
                    <div key={index} className="bc-control-item">
                        <label className="bc-control-label">{control.label}</label>
                        <PositionGrid
                            fieldName={control.fieldName}
                            currentValue={editData[control.fieldName] || control.defaultValue || 'center'}
                            onChange={(field, val) => handleInputChange({ target: { name: field, value: val } })}
                        />
                    </div>
                );

            case 'image-effect':
                return (
                    <div key={index} className="bc-control-item">
                        <label className="bc-control-label">{control.label}</label>
                        <ImageEffects
                            fieldName={control.fieldName}
                            currentValue={editData[control.fieldName] || control.defaultValue || 'none'}
                            onChange={(field, val) => handleInputChange({ target: { name: field, value: val } })}
                        />
                    </div>
                );

            case 'custom':
                // ✅ CUSTOM COMPONENTS - für spezielle UI Elemente wie Color Picker
                if (control.component) {
                    const CustomComponent = control.component;
                    return (
                        <div key={index} className="bc-control-item bc-custom-control">
                            <CustomComponent
                                {...(control.componentProps || {})}
                            />
                        </div>
                    );
                }
                return null;

            default:
                return null;
        }
    };

    const isImageVisible = showToggle ? toggleChecked : true;
    const hasControls = controls && controls.length > 0;

    return (
        <div className={`bc-image-container ${compact ? 'bc-compact' : ''} ${theme}`}>
            {/* Header with Toggle */}
            <div className="bc-image-header">
                <div className="bc-image-header-content">
                    <div className="bc-image-icon">
                        <i className="fa fa-image"></i>
                    </div>
                    <div className="bc-image-title">{label}</div>
                </div>
                
                <div className="bc-header-controls">
                    {showToggle && (
                        <ToggleSwitch
                            fieldName={toggleFieldName}
                            checked={toggleChecked}
                            onChange={onToggleChange}
                            size="small"
                            color="#5C80BC"
                            showLabel={false}
                        />
                    )}
                    
                    {hasControls && isImageVisible && (
                        <button
                            type="button"
                            className={`bc-controls-toggle ${controlsExpanded ? 'bc-expanded' : ''}`}
                            onClick={toggleControls}
                            title="Toggle Controls"
                        >
                            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                                <path 
                                    d="M4 6L8 10L12 6" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Upload Area - Only show if image is visible */}
            {isImageVisible && (
                <div className="bc-image-content">
                    <div 
                        className={`bc-upload-area ${dragOver ? 'bc-drag-over' : ''} ${isUploading ? 'bc-uploading' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                        />

                        {isUploading ? (
                            <div className="bc-upload-loading">
                                <div className="bc-loading-spinner">
                                    <div className="bc-spinner-ring"></div>
                                    <div className="bc-spinner-ring bc-spinner-ring-animated"></div>
                                </div>
                                <span className="bc-loading-text">Uploading...</span>
                            </div>
                        ) : imagePreview ? (
                            <div className="bc-image-preview">
                                <img 
                                    src={imagePreview} 
                                    alt={label}
                                    className="bc-preview-img"
                                />
                                <div className="bc-image-overlay">
                                    <div className="bc-overlay-content">
                                        <i className="fa fa-camera"></i>
                                        <span>Click or drag to change</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bc-upload-placeholder">
                                <div className="bc-upload-icon">
                                    <i className="fa fa-cloud-upload-alt"></i>
                                </div>
                                <div className="bc-upload-text">
                                    <span className="bc-primary-text">Click to upload</span>
                                    <span className="bc-secondary-text">or drag and drop</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Status Indicator with Text - Below Upload Area */}
                    <div className="bc-status-section">
                        <div className={`bc-status-indicator ${isImageVisible ? (imagePreview ? 'bc-active' : 'bc-pending') : 'bc-inactive'}`}>
                            <div className="bc-status-dot"></div>
                            <span className="bc-status-text">
                                {isImageVisible ? 
                                    (imagePreview ? 'Image uploaded' : 'No image uploaded') : 
                                    'Image hidden'
                                }
                            </span>
                        </div>
                    </div>

                    {/* Controls Section - Collapsible */}
                    {hasControls && controlsExpanded && (
                        <div className="bc-controls-section">
                            <div className="bc-controls-content">
                                {controls.map((control, index) => renderControl(control, index))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ImageContainer;